import React, { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import CreatableSelect from "react-select/creatable";
import Button from "../Button";
import { LocationSelect } from "../LocationSelect";
import { LinkedMemberSelect } from "../LinkedMemberSelect";
import ProfileImagePreview from "../ProfileImagePreview";
import { MarkdownEditor } from "../MarkdownEditor";
import { mapToOptions } from "../../utils/converters";
import {
  defaultTypeOptions,
  formFieldLabels,
  memberLabelsOnOfferings,
} from "../../strings/strings";
import { cap } from "../../utils/strings";
import {
  Option,
  ListingFormState,
  LocationData,
  ListingWithDetails,
  ItemType,
  OfferingType,
  LinkedMember,
} from "../../types";
import { LocationMap } from "../../store/slices/locationsSlice";

interface InitialListingData {
  id?: string;
  name?: string;
  itemType?: ItemType;
  subType?: string;
  description?: string;
  url?: string;
  email?: string;
  socialLinks?: Array<{ platform: string; url: string }>;
  imageUrl?: string;
  imageUrlOriginal?: string;
  coverImageUrl?: string;
  coverImageUrlOriginal?: string;
  startDate?: string;
  endDate?: string;
  dateLabel?: string;
  approximateDate?: string;
  recurrenceDescription?: string;
  isOnline?: boolean;
}

interface ListingFormProps {
  onSubmit: (data: FormData) => Promise<any>;
  onClose: () => void;
  itemType: ItemType;
  members: ListingWithDetails[];
  existingLocations: LocationMap;
  prefilledEmail?: string;
  fromInvitation?: boolean;
  initialData?: InitialListingData;
  isEditing?: boolean;
}

const ListingForm: React.FC<ListingFormProps> = ({
  onSubmit,
  onClose,
  itemType,
  members,
  existingLocations,
  prefilledEmail,
  fromInvitation = false,
  initialData,
  isEditing = false,
}) => {
  const { user } = useAuth();
  const isMember = itemType === "person";
  const isOffering = !isMember;
  const currentUserListingId = user?.primaryListingId || "";

  const getInstagramFromSocialLinks = (
    socialLinks?: Array<{ platform: string; url: string }>
  ): string => {
    if (!socialLinks) return "";
    const instagram = socialLinks.find((link) => link.platform === "instagram");
    return instagram?.url || "";
  };

  const [formData, setFormData] = useState<ListingFormState>({
    name: initialData?.name || "",
    itemType,
    subType: initialData?.subType || "",
    locations: [] as LocationData[],
    description: initialData?.description || "",
    url: initialData?.url || "",
    email: initialData?.email || prefilledEmail || "",
    instagram: getInstagramFromSocialLinks(initialData?.socialLinks),
    connections: [],
    profileImage: null,
    profileImagePreview: initialData?.imageUrl || "",
    profileImageUrl: initialData?.imageUrl || "",
    profileImageUrlOriginal: initialData?.imageUrlOriginal || "",
    creators: [] as LinkedMember[],
    collaborators: [] as LinkedMember[],
    participants: [] as LinkedMember[],
    coordinates: null as { lat: number; lng: number } | null,
    isRecurring: !!initialData?.recurrenceDescription,
    isApproximate: !!initialData?.approximateDate,
    isOnline: initialData?.isOnline || false,
    startDate: initialData?.startDate || "",
    endDate: initialData?.endDate || "",
    dateLabel: initialData?.dateLabel || "",
    approximateDate: initialData?.approximateDate || "",
    recurrenceDescription: initialData?.recurrenceDescription || "",
    conditionsAgreed: isEditing,
    privacyPolicyAgreed: isEditing,
    subscribeToDigest: false,
    coverImage: null,
    coverImagePreview: initialData?.coverImageUrl || "",
    coverImageUrl: initialData?.coverImageUrl || "",
    coverImageUrlOriginal: initialData?.coverImageUrlOriginal || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<{
    creators: string[];
    collaborators: string[];
    participants: string[];
  }>({
    creators: [],
    collaborators: [],
    participants: [],
  });

  const strings = isOffering ? formFieldLabels[itemType as OfferingType] : null;
  const relationStrings = isOffering
    ? memberLabelsOnOfferings[itemType as OfferingType]
    : null;

  const subTypeOptions: Option[] = mapToOptions(
    defaultTypeOptions[itemType] || []
  );

  const creatorInitialized = useRef(false);

  useEffect(() => {
    if (
      isOffering &&
      currentUserListingId &&
      members.length > 0 &&
      !creatorInitialized.current
    ) {
      const userMember = members.find((m) => m.id === currentUserListingId);
      if (userMember) {
        creatorInitialized.current = true;
        setFormData((prev) => ({
          ...prev,
          creators: [
            {
              id: userMember.id,
              name: userMember.name,
              isRegistered: true,
              imageUrl: userMember.imageUrl,
              role: "creator",
            },
          ],
        }));
      }
    }
  }, [isOffering, currentUserListingId, members]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = isMember ? "Name is required" : "Title is required";
    }

    if (
      formData.locations.length === 0 &&
      (!isOffering || !formData.isOnline)
    ) {
      newErrors.locations =
        "At least one location is required (or select Online/Virtual)";
    }

    if (!formData.description.trim() && isMember) {
      newErrors.description = "Description is required";
    }

    if (isMember) {
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          newErrors.email = "Please enter a valid email address";
        }
      }
    }

    if (isOffering) {
      if (!formData.isRecurring && !formData.startDate) {
        newErrors.startDate = "Start date is required";
      }

      if (formData.isApproximate && !formData.approximateDate.trim()) {
        newErrors.approximateDate = "Approximate date description is required";
      }

      if (formData.isRecurring && !formData.recurrenceDescription.trim()) {
        newErrors.recurrenceDescription = "Recurrence description is required";
      }

      if (!formData.conditionsAgreed) {
        newErrors.conditionsAgreed = "You must agree to the conditions";
      }

      if (!formData.privacyPolicyAgreed) {
        newErrors.privacyPolicyAgreed = "You must agree to the privacy policy";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSubTypeChange = useCallback((selectedOption: any) => {
    const value = selectedOption
      ? selectedOption.__isNew__
        ? selectedOption.label
        : selectedOption.value
      : "";
    setFormData((prev) => ({ ...prev, subType: value }));
  }, []);

  const handleCoverImageChange = useCallback(
    (file: File | null, url?: string) => {
      setFormData((prev) => ({
        ...prev,
        coverImage: file,
        coverImagePreview: url || "",
      }));
    },
    []
  );

  const handleProfileImageChange = useCallback(
    (file: File | null, url?: string) => {
      setFormData((prev) => ({
        ...prev,
        profileImage: file,
        profileImagePreview: url || "",
      }));
    },
    []
  );

  const handleCloudinaryCoverUpload = useCallback(
    (croppedUrl: string, originalUrl: string) => {
      setFormData((prev) => ({
        ...prev,
        coverImageUrl: croppedUrl,
        coverImageUrlOriginal: originalUrl,
        coverImagePreview: croppedUrl,
      }));
    },
    []
  );

  const handleCloudinaryProfileUpload = useCallback(
    (croppedUrl: string, originalUrl: string) => {
      setFormData((prev) => ({
        ...prev,
        profileImageUrl: croppedUrl,
        profileImageUrlOriginal: originalUrl,
        profileImagePreview: croppedUrl,
      }));
    },
    []
  );

  const handleConnectionsChange = useCallback(
    (
      linkedMembers: LinkedMember[],
      emailInvites: string[] = [],
      fieldName: "creators" | "collaborators" | "participants"
    ) => {
      setFormData((prev) => ({ ...prev, [fieldName]: linkedMembers }));
      setPendingInvitations((prev) => ({ ...prev, [fieldName]: emailInvites }));
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = new FormData();

      submissionData.append("itemType", formData.itemType);
      submissionData.append("name", formData.name);
      submissionData.append("subType", formData.subType);
      submissionData.append("description", formData.description);
      submissionData.append("url", formData.url);

      if (isMember) {
        submissionData.append("email", formData.email);
        submissionData.append(
          "subscribeToDigest",
          formData.subscribeToDigest.toString()
        );

        if (formData.instagram.trim()) {
          const instagramHandle = formData.instagram.trim().replace(/^@/, "");
          const socialLinks = [{ platform: "instagram", url: instagramHandle }];
          submissionData.append("socialLinks", JSON.stringify(socialLinks));
        }

        if (formData.profileImageUrl) {
          submissionData.append("profileImageUrl", formData.profileImageUrl);
          submissionData.append(
            "profileImageUrlOriginal",
            formData.profileImageUrlOriginal
          );
        } else if (formData.profileImage) {
          submissionData.append("profileImage", formData.profileImage);
        }

        if (formData.connections.length > 0) {
          const connectionsData = formData.connections.map((conn) => ({
            id: conn.id,
            name: conn.name,
            role: conn.role || "collaborator",
          }));
          submissionData.append("connections", JSON.stringify(connectionsData));
        }
      }

      if (isOffering) {
        submissionData.append("type", formData.itemType);

        if (formData.isOnline) {
          submissionData.append("location", "Online/Virtual");
        } else {
          submissionData.append(
            "locations",
            JSON.stringify(formData.locations)
          );
          if (formData.locations.length > 0) {
            submissionData.append("location", formData.locations[0].placeName);
          }
        }

        if (formData.coordinates && !formData.isOnline) {
          submissionData.append("lat", formData.coordinates.lat.toString());
          submissionData.append("lng", formData.coordinates.lng.toString());
        }

        if (formData.isRecurring) {
          submissionData.append(
            "recurrenceDescription",
            formData.recurrenceDescription
          );
          submissionData.append("startDate", "");
          submissionData.append("endDate", "");
          submissionData.append("approximateDate", "");
        } else {
          submissionData.append("startDate", formData.startDate || "");
          if (formData.endDate)
            submissionData.append("endDate", formData.endDate);
          submissionData.append("recurrenceDescription", "");
          if (formData.isApproximate) {
            submissionData.append("approximateDate", formData.approximateDate);
          } else {
            submissionData.append("approximateDate", "");
          }
        }

        if (formData.dateLabel)
          submissionData.append("dateLabel", formData.dateLabel);

        submissionData.append("imageCredits", "");
        submissionData.append(
          "conditionsAgreed",
          formData.conditionsAgreed.toString()
        );
        submissionData.append(
          "privacyPolicyAgreed",
          formData.privacyPolicyAgreed.toString()
        );

        submissionData.append(
          "creators",
          JSON.stringify(
            formData.creators.map((member) => ({
              id: member.id,
              name: member.name,
              role: "creator",
            }))
          )
        );
        submissionData.append(
          "collaborators",
          JSON.stringify(
            formData.collaborators.map((member) => ({
              id: member.id,
              name: member.name,
              role: "collaborator",
            }))
          )
        );
        submissionData.append(
          "participants",
          JSON.stringify(
            formData.participants.map((member) => ({
              id: member.id,
              name: member.name,
              role: "participant",
            }))
          )
        );
      }

      if (!isMember || formData.locations.length > 0) {
        submissionData.append("locations", JSON.stringify(formData.locations));
        if (formData.locations.length > 0) {
          submissionData.append("location", formData.locations[0].placeName);
        }
      }

      if (formData.coverImageUrl) {
        submissionData.append("coverImageUrl", formData.coverImageUrl);
        submissionData.append(
          "coverImageUrlOriginal",
          formData.coverImageUrlOriginal
        );
      } else if (formData.coverImage) {
        const fieldName = isMember ? "coverImage" : "offeringImage";
        submissionData.append(fieldName, formData.coverImage);
      }

      const result = await onSubmit(submissionData);

      if (isOffering && result && result.eventId) {
        const allInvitations = [
          ...pendingInvitations.creators.map((email) => ({
            email,
            role: "creator",
          })),
          ...pendingInvitations.collaborators.map((email) => ({
            email,
            role: "collaborator",
          })),
          ...pendingInvitations.participants.map((email) => ({
            email,
            role: "participant",
          })),
        ];

        const inviterName = formData.creators[0]?.name || formData.name;

        for (const invitation of allInvitations) {
          try {
            await fetch("/api/invitations", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: invitation.email,
                offeringId: result.eventId,
                offeringName: formData.name,
                role: invitation.role,
                inviterName: inviterName,
              }),
            });
          } catch (inviteError) {
            console.error(
              `Failed to send invitation to ${invitation.email}:`,
              inviteError
            );
          }
        }
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSubType = formData.subType
    ? subTypeOptions.find((opt) => opt.value === formData.subType) || {
        value: formData.subType,
        label: formData.subType,
      }
    : null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            {isEditing
              ? `Edit ${isMember ? "Profile" : itemType}`
              : isMember
                ? "Join the Network"
                : `Add ${itemType}`}
          </h2>
          {!fromInvitation && (
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className={isMember ? "service-form" : "offering-form"}
        >
          <ProfileImagePreview
            coverImageUrl={formData.coverImagePreview}
            profileImageUrl={formData.profileImagePreview}
            name={formData.name || undefined}
            onCoverUpload={handleCloudinaryCoverUpload}
            onProfileUpload={handleCloudinaryProfileUpload}
            showProfileImage={isMember}
          />

          <div className="form-group">
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={
                isMember
                  ? "Name: e.g., Sarah Chen or Dream Research Institute"
                  : strings
                    ? `${strings.name} *`
                    : "Name *"
              }
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>

          <div className="form-group">
            <CreatableSelect
              value={selectedSubType}
              onChange={handleSubTypeChange}
              options={subTypeOptions}
              placeholder={
                isMember
                  ? "Select or create a member type..."
                  : strings
                    ? strings.subType
                    : "Select type..."
              }
              noOptionsMessage={() =>
                isMember
                  ? "Type to create a custom member type"
                  : "Type to create a custom type"
              }
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                  border: "1px solid #cbd5e0",
                  borderRadius: "0px",
                  "&:hover": { borderColor: "#a0aec0" },
                  "&:focus-within": {
                    borderColor: "#4299e1",
                    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
                  },
                }),
                placeholder: (base) => ({ ...base, color: "#999" }),
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {isMember
                ? "Description * (Who you are, what you do, and your connection to dreams)"
                : strings
                  ? strings.description
                  : "Description"}
            </label>
            <MarkdownEditor
              value={formData.description}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, description: value }))
              }
              features={[
                "bold",
                "italic",
                "heading",
                "bulletList",
                "orderedList",
                "link",
              ]}
              placeholder="Start typing..."
            />
            {errors.description && (
              <div className="error-message">{errors.description}</div>
            )}
          </div>

          {isOffering && relationStrings && (
            <>
              <div className="form-group">
                <label>{cap(relationStrings.creator + "s")}</label>
                <LinkedMemberSelect
                  value={formData.creators}
                  onChange={(linkedMembers, emailInvites) =>
                    handleConnectionsChange(
                      linkedMembers,
                      emailInvites || [],
                      "creators"
                    )
                  }
                  members={members}
                  role="creator"
                  creatable={true}
                  placeholder="Select members or type email to invite..."
                  lockedIds={currentUserListingId ? [currentUserListingId] : []}
                />
                {pendingInvitations.creators.length > 0 && (
                  <span className="info-text">
                    Will send invitations to:{" "}
                    {pendingInvitations.creators.join(", ")}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>{cap(relationStrings.collaborator + "s")}</label>
                <LinkedMemberSelect
                  value={formData.collaborators}
                  onChange={(linkedMembers, emailInvites) =>
                    handleConnectionsChange(
                      linkedMembers,
                      emailInvites || [],
                      "collaborators"
                    )
                  }
                  members={members}
                  role="collaborator"
                  creatable={true}
                  placeholder="Select members or type email to invite..."
                />
                {pendingInvitations.collaborators.length > 0 && (
                  <span className="info-text">
                    Will send invitations to:{" "}
                    {pendingInvitations.collaborators.join(", ")}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>{cap(relationStrings.participant + "s")}</label>
                <LinkedMemberSelect
                  value={formData.participants}
                  onChange={(linkedMembers, emailInvites) =>
                    handleConnectionsChange(
                      linkedMembers,
                      emailInvites || [],
                      "participants"
                    )
                  }
                  members={members}
                  role="participant"
                  creatable={true}
                  placeholder="Select members or type email to invite..."
                />
                {pendingInvitations.participants.length > 0 && (
                  <span className="info-text">
                    Will send invitations to:{" "}
                    {pendingInvitations.participants.join(", ")}
                  </span>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label>Location *</label>
            {isOffering && (
              <div className="location-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isOnline}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isOnline: e.target.checked,
                      }))
                    }
                  />
                  {strings?.isOnline || "Online/Virtual"}
                </label>
              </div>
            )}
            {(!isOffering || !formData.isOnline) && (
              <LocationSelect
                value={formData.locations}
                onChange={(locations) =>
                  setFormData((prev) => ({
                    ...prev,
                    locations: Array.isArray(locations) ? locations : [],
                  }))
                }
                placeholder="Search and select locations..."
                error={!!errors.locations}
                isMulti={true}
                existingLocations={existingLocations}
              />
            )}
            {errors.locations && (
              <span className="error-text">{errors.locations}</span>
            )}
          </div>

          {isOffering && (
            <>
              <div className="form-group">
                <label>Date</label>
                <div className="form-group">
                  <div className="location-options">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isRecurring}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isRecurring: e.target.checked,
                            isApproximate: e.target.checked
                              ? false
                              : prev.isApproximate,
                            startDate: e.target.checked ? "" : prev.startDate,
                            endDate: e.target.checked ? "" : prev.endDate,
                          }))
                        }
                      />
                      Regularly Recurring Offering
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isApproximate}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isApproximate: e.target.checked,
                            isRecurring: e.target.checked
                              ? false
                              : prev.isRecurring,
                          }))
                        }
                      />
                      Approximate Date
                    </label>
                  </div>
                </div>

                {formData.isRecurring ? (
                  <div className="form-group">
                    <label htmlFor="recurrenceDescription">
                      Recurrence Description *
                    </label>
                    <input
                      type="text"
                      id="recurrenceDescription"
                      value={formData.recurrenceDescription}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          recurrenceDescription: e.target.value,
                        }))
                      }
                      placeholder="e.g., 'Every Tuesday at 7pm' or 'First Saturday of each month'"
                      className={errors.recurrenceDescription ? "error" : ""}
                    />
                    {errors.recurrenceDescription && (
                      <span className="error-text">
                        {errors.recurrenceDescription}
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    {formData.isApproximate && (
                      <div className="form-group">
                        <label htmlFor="approximateDate">
                          Approximate Date Description *
                        </label>
                        <input
                          type="text"
                          id="approximateDate"
                          value={formData.approximateDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              approximateDate: e.target.value,
                            }))
                          }
                          placeholder="e.g., 'Early summer 2025' or 'Late fall, depends on venue'"
                          className={errors.approximateDate ? "error" : ""}
                        />
                        {errors.approximateDate && (
                          <span className="error-text">
                            {errors.approximateDate}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="startDate">
                          {strings?.startDate || "Start Date"} *
                        </label>
                        <input
                          type="date"
                          id="startDate"
                          value={formData.startDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }))
                          }
                          className={errors.startDate ? "error" : ""}
                        />
                        {errors.startDate && (
                          <span className="error-text">{errors.startDate}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="endDate">
                          {strings?.endDate || "End Date"}
                        </label>
                        <input
                          type="date"
                          id="endDate"
                          value={formData.endDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="url">
              {isMember ? "Website or Portfolio" : strings?.url || "Website"}
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              placeholder="https://..."
            />
          </div>

          {isMember && (
            <>
              <div className="form-group">
                <label htmlFor="email">Contact Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="instagram">Instagram</label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  placeholder="@yourhandle or yourhandle"
                />
              </div>

              <div className="form-group">
                <label>Network Connections</label>
                <LinkedMemberSelect
                  value={formData.connections}
                  onChange={(linkedMembers) => {
                    setFormData((prev) => ({
                      ...prev,
                      connections: linkedMembers,
                    }));
                  }}
                  members={members}
                  role="collaborator"
                  creatable={false}
                  excludeName={formData.name}
                  placeholder="Select existing members you'd like to connect with (max 20)..."
                  maxSelections={20}
                />
                <small
                  style={{
                    color: "#718096",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Select existing network members that you collaborate with or
                  are connected to. Maximum 20 connections.
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.subscribeToDigest}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        subscribeToDigest: e.target.checked,
                      }))
                    }
                  />
                  Send me weekly email updates about new offerings
                </label>
              </div>
            </>
          )}

          {isOffering && (
            <>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.conditionsAgreed}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        conditionsAgreed: e.target.checked,
                      }))
                    }
                    className={errors.conditionsAgreed ? "error" : ""}
                  />
                  I agree to the terms and conditions (link TBD)
                </label>
                {errors.conditionsAgreed && (
                  <span className="error-text">{errors.conditionsAgreed}</span>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.privacyPolicyAgreed}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        privacyPolicyAgreed: e.target.checked,
                      }))
                    }
                    className={errors.privacyPolicyAgreed ? "error" : ""}
                  />
                  I agree to the privacy policy (link TBD)
                </label>
                {errors.privacyPolicyAgreed && (
                  <span className="error-text">
                    {errors.privacyPolicyAgreed}
                  </span>
                )}
              </div>
            </>
          )}

          <div className="form-actions">
            {!fromInvitation && (
              <button type="button" onClick={onClose} className="cancel-button">
                Cancel
              </button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isEditing
                ? "Save Changes"
                : isMember
                  ? "Join Network"
                  : "Submit Offering"}
            </Button>
          </div>

          {isMember && (
            <p className="form-note">
              * Required fields. Your membership application will be reviewed
              before you appear in the network.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ListingForm;
